import Button from "@components/Button"
import Layout from "@components/Layout"
import Mascot, { MascotState } from "@components/Mascot"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"
import { twMerge } from "tailwind-merge"

interface Props {
  translationKey: string
  options: readonly string[]
  headingKey?: string
  subtitleKey?: string
  noneOptionKey?: string
}

export default function MultiSelectPage({
  translationKey,
  options,
  headingKey = "heading",
  subtitleKey,
  noneOptionKey,
}: Props) {
  const { t } = useTranslation()
  const submit = useSubmit()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [mascotState, setMascotState] = useState<MascotState>("idle")

  useEffect(() => {
    if (selected.size > 0) {
      setMascotState("writing")
      const timer = setTimeout(() => setMascotState("idle"), 800)
      return () => clearTimeout(timer)
    }
    setMascotState("idle")
  }, [selected.size])

  const handleToggle = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (value === noneOptionKey) {
        return next.has(value) ? new Set() : new Set([value])
      }
      next.delete(noneOptionKey ?? "")
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit(event.currentTarget)
  }

  const heading = t(`pages.onboarding.${translationKey}.${headingKey}`)
  const subtitle = subtitleKey
    ? t(`pages.onboarding.${translationKey}.${subtitleKey}`)
    : undefined

  return (
    <Layout>
      <div className="mt-4 flex items-center gap-3">
        <Mascot state={mascotState} />
        <div className="speech-bubble flex-1">
          <h1 className="text-lg font-bold">{heading}</h1>
        </div>
      </div>

      {subtitle && <p className="custom-subheading mt-3">{subtitle}</p>}

      <Form className="custom-form" method="post" onSubmit={handleSubmit}>
        <div className="mt-6 flex grow flex-col justify-center space-y-3">
          {options.map((option) => {
            const isSelected = selected.has(option)
            return (
              <label
                key={option}
                className={twMerge(
                  "custom-checkbox-input flex cursor-pointer items-center gap-3",
                  isSelected && "custom-checkbox-input-selected",
                )}
              >
                <div
                  className={twMerge(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-all",
                    isSelected
                      ? "border-custom-teal bg-custom-teal"
                      : "border-gray-300 bg-white",
                  )}
                >
                  {isSelected && (
                    <svg
                      className="h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <span className="text-custom-charcoal font-semibold">
                  {t(`pages.onboarding.${translationKey}.${option}`)}
                </span>
                <input
                  type="checkbox"
                  name={translationKey}
                  value={option}
                  checked={isSelected}
                  onChange={() => handleToggle(option)}
                  className="hidden"
                />
              </label>
            )
          })}
        </div>

        <div className="sticky bottom-0 -mx-5 bg-gradient-to-t from-white from-70% to-transparent px-5 pt-8 pb-4">
          <Button
            title={t("shared.continue")}
            type="submit"
            disabled={selected.size === 0}
          />
        </div>
      </Form>
    </Layout>
  )
}
